import React from 'react'
import makeStyles from '@material-ui/core/styles/makeStyles'
import Grid from '@material-ui/core/Grid'
import ClearFiltersIcon from 'assets/icons/photo/ClearFiltersIcon'
import Typography from '@material-ui/core/Typography'
import { useTranslation } from 'react-i18next'
import useMediaQuery from '@material-ui/core/useMediaQuery'
import useFilterCollection from 'photo-module/filters-bar/core/filters.hooks'
import dayjs from 'dayjs'
import localeData from 'dayjs/plugin/localeData'
import localizedFormat from 'dayjs/plugin/localizedFormat'
import relativeTime from 'dayjs/plugin/relativeTime'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import useLanguage from 'shared-module/components/language/useLanguage'
import { getLocaleFormats, capitalizeFirstLetter } from '../utils/dateFormatUtils'

// Import locales
import 'dayjs/locale/en'
import 'dayjs/locale/fr'
import 'dayjs/locale/de'
import 'dayjs/locale/es'

// Enable dayjs plugins
dayjs.extend(localeData)
dayjs.extend(localizedFormat)
dayjs.extend(relativeTime)
dayjs.extend(customParseFormat)

const useStyles = makeStyles(theme => ({
  clearIcon: {
    verticalAlign: 'middle',
    display: 'inline-flex',
    marginBottom: -12,
    marginRight: -10,
  },
  selectedItem: {
    border: `solid 1px ${theme.palette.background.paper}`,
    borderRadius: '4px',
    marginRight: 10,
    padding: '2px 10px 2px 10px',
  },
}))

const SelectedFiltersList = () => {
  const classes = useStyles()
  const { t } = useTranslation()
  const isMobile = useMediaQuery(theme => theme.breakpoints.down('xs'))
  const {
    selectedFilters,
    deleteSelectedFilter,
  } = useFilterCollection()
  const language = useLanguage()

  // Set dayjs locale based on user language
  React.useEffect(() => {
    dayjs.locale(language)
  }, [language])

  // Helper function to format dates according to language conventions
  const getLocalizedDateFormat = (date, format) => {
    return date.format(format)
  }

  if (isMobile) {
    return null
  }

  const renderFilters = selectedFilters && selectedFilters.map((value) => {
    // Special handling for dateRange filters
    if (value.startsWith('dateRange:')) {
      const dateValue = value.split(':')[1].trim()
      // Parse the date range string
      const [startDateStr, endDateStr] = dateValue.split(' - ')
      const startDate = dayjs(startDateStr).locale(language)
      const endDate = dayjs(endDateStr).locale(language)

      // Get locale-specific format patterns
      const formats = getLocaleFormats(language)

      // Format dates based on whether they're in the same year or not
      // Using localized date formatting
      let formattedDateRange
      const today = dayjs().startOf('day')
      const yesterday = today.subtract(1, 'day')
      const isToday = day => day.isSame(today, 'day')
      const isYesterday = day => day.isSame(yesterday, 'day')

      // Check for common patterns like "Today", "Yesterday", etc.
      if (isToday(startDate) && isToday(endDate)) {
        formattedDateRange = t('app:filters.dateRange.today', { defaultValue: 'Today' })
      } else if (isYesterday(startDate) && isYesterday(endDate)) {
        formattedDateRange = t('app:filters.dateRange.yesterday', { defaultValue: 'Yesterday' })
      } else if (startDate.year() === endDate.year() && startDate.month() === endDate.month()) {
        // Same month and year: Use locale-specific format
        formattedDateRange = t('app:filters.dateRange.sameMonthYear', {
          month: capitalizeFirstLetter(startDate.format('MMMM')),
          startDay: startDate.format('D'),
          endDay: endDate.format('D'),
          year: endDate.format('YYYY'),
          defaultValue: '{{month}} {{startDay}}-{{endDay}}, {{year}}',
        })
      } else if (startDate.year() === endDate.year()) {
        // Same year, different month: Use locale-specific month-day format
        formattedDateRange = t('app:filters.dateRange.sameYear', {
          startDate: startDate.format(formats.monthDayFull),
          endDate: endDate.format(formats.monthDayFull),
          year: endDate.format('YYYY'),
          defaultValue: '{{startDate}} - {{endDate}}, {{year}}',
        })
      } else {
        // Different years: Use locale-specific long date format
        formattedDateRange = t('app:filters.dateRange.differentYears', {
          startDate: startDate.format(formats.longDate),
          endDate: endDate.format(formats.longDate),
          defaultValue: '{{startDate}} - {{endDate}}',
        })
      }

      return (
        <Grid item className={classes.selectedItem} key={value} onClick={() => deleteSelectedFilter(value)}>
          <ClearFiltersIcon className={classes.clearIcon} />
          <Typography
            component="span"
            variant="body2"
            color="secondary"
          >
            { formattedDateRange }
          </Typography>
        </Grid>
      )
    }

    // Standard filter handling
    return (
      <Grid item className={classes.selectedItem} key={value} onClick={() => deleteSelectedFilter(value)}>
        <ClearFiltersIcon className={classes.clearIcon} />
        <Typography
          component="span"
          variant="body2"
          color="secondary"
        >
          { t(`app:filters.tags.${value}`) }
        </Typography>
      </Grid>
    )
  })
  return (
    <Grid
      container
      direction="row"
      justifyContent="flex-start"
      alignItems="center"
      style={{ margin: '10px 20px 10px' }}
    >
      { renderFilters }
    </Grid>
  )
}

export default SelectedFiltersList
